import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import {HOME_RECENT_ITEMS, SHOP_ITEM_GRID_CLASS, SHOP_ROUTE, TELEGRAM_URL} from '../../util/Constants';
import Seo from '../seo/Seo';
import {getPublicSiteUrl} from '../../util/siteUrl';
import {Context} from '../../index';
import {fetchDataUnauth, fetchItemsPage} from '../../http/itemsAPI';
import {CATEGORIES} from '../../http/ApiEndpoints';
import ItemCard from '../items/ItemCard';
import {catalogItemId, hasCatalogItemId} from '../../util/catalogItemId';
import carusel1 from '../../assets/carusel1.jpg';
import carusel2 from '../../assets/carusel2.jpg';
import carusel3 from '../../assets/carusel3.jpg';

const SLIDES = [
    {name: '1', src: carusel1, caption: 'Ароматы, вдохновленные природой'},
    {name: '2', src: carusel2, caption: 'Экологично от фитиля до упаковки'},
    {name: '3', src: carusel3, caption: 'Ручная работа'},
];

const Landing = observer(() => {
    const {items} = useContext(Context);
    const location = useLocation();
    const slides = SLIDES;
    const siteUrl = getPublicSiteUrl();
    const landingJsonLd = useMemo(() => {
        if (!siteUrl) {
            return null;
        }
        return {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Lutica',
            url: siteUrl,
            // Для поисковиков (не отображается как обычный текст на странице): явный тип оффера / бренда
            description:
                'Интернет-магазин Lutica (Лютика): натуральные ароматические свечи и соевые свечи ручной работы, ароматы для дома, доставка по России.',
        };
    }, [siteUrl]);
    const [active, setActive] = useState(0);
    const [recent, setRecent] = useState([]);
    /** После ответа API «новинок» — высота страницы стабилизируется, якорь #delivery можно прокрутить точно. */
    const [recentFetchDone, setRecentFetchDone] = useState(false);

    useEffect(() => {
        const t = window.setInterval(() => {
            setActive((i) => (i + 1) % slides.length);
        }, 6500);
        return () => window.clearInterval(t);
    }, [slides.length]);

    useEffect(() => {
        if (items.categories.length === 0) {
            fetchDataUnauth(CATEGORIES).then((data) => items.setCategories(data));
        }
    }, [items]);

    useEffect(() => {
        let cancelled = false;
        setRecentFetchDone(false);
        fetchItemsPage({
            page: 0,
            size: HOME_RECENT_ITEMS,
            sort: 'id,desc',
            onlyAvailable: true,
        })
            .then((data) => {
                if (cancelled) return;
                setRecent(data.items || []);
            })
            .catch(() => {
                if (!cancelled) setRecent([]);
            })
            .finally(() => {
                if (!cancelled) setRecentFetchDone(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const hash = location.hash || '';
        if (hash !== '#delivery') return undefined;
        const el = () => document.getElementById('delivery');
        const scroll = (behavior) => {
            el()?.scrollIntoView({behavior, block: 'start'});
        };
        const timers = [];
        timers.push(window.setTimeout(() => scroll('smooth'), 80));
        if (recentFetchDone) {
            timers.push(window.setTimeout(() => scroll('auto'), 160));
        }
        return () => {
            timers.forEach((id) => window.clearTimeout(id));
        };
    }, [location.pathname, location.hash, recentFetchDone, recent]);

    return (
        <div className="pb-6 sm:pb-8">
            <Seo
                description="Лютика — ароматические и соевые свечи ручной работы для дома, натуральные ароматы. Каталог, доставка по России через Яндекс ПВЗ или по договорённости."
                pathname="/"
                jsonLd={landingJsonLd}
            />
            <section className="w-full pt-2">
                <div className="relative w-full overflow-hidden rounded-3xl border border-stone-200/70 bg-cream-100 shadow-soft">
                    <div className="relative aspect-[21/9] min-h-[160px] w-full sm:aspect-[2/1] sm:min-h-[200px]">
                        {slides.map((slide, i) => (
                            <img
                                key={slide.name}
                                src={slide.src}
                                alt=""
                                className={`absolute inset-0 h-full w-full object-cover object-[center_80%] transition-opacity duration-500 ${
                                    i === active ? 'opacity-100' : 'pointer-events-none opacity-0'
                                }`}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                }}
                            />
                        ))}
                        <div
                            className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[52%] min-h-[7.25rem] bg-gradient-to-t from-bark-900 via-bark-900/82 via-30% to-bark-900/0 sm:h-[48%] sm:min-h-[7.75rem] sm:via-35%"
                            aria-hidden
                        />
                        <div
                            className="pointer-events-none absolute bottom-11 left-0 right-0 z-[2] px-3 sm:bottom-14 sm:px-5"
                            aria-live="polite"
                        >
                            <p className="text-center text-xs font-semibold leading-snug text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)] sm:text-sm md:text-base">
                                {slides[active]?.caption}
                            </p>
                        </div>
                        <div className="absolute bottom-2 left-0 right-0 z-[2] flex justify-center gap-1 px-2 sm:bottom-3 sm:gap-2">
                            {slides.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    aria-label={`Slide ${i + 1}`}
                                    onClick={() => setActive(i)}
                                    className="touch-manipulation p-2 sm:p-2.5"
                                >
                                    <span
                                        className={`block h-2.5 rounded-full transition-all sm:h-3 ${
                                            i === active ? 'w-9 bg-white sm:w-10' : 'w-2.5 bg-white/55 sm:w-3'
                                        }`}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="mx-auto mt-10 max-w-3xl px-1 text-center" aria-labelledby="landing-heading">
                <h1
                    id="landing-heading"
                    className="text-2xl font-semibold tracking-tight text-bark-900 sm:text-3xl"
                >
                    Лютика — для дома, где ценят природу
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-bark-700 sm:text-base">
                    Мы создаём небольшой, но продуманный ассортимент натуральных свечей ручной работы и ароматов для дома.
                    Листайте вниз, чтобы увидеть новинки, или переходите в каталог.
                </p>
            </section>

            <section className="mt-16 border-t border-stone-200/60 pt-12">
                <div className="flex flex-col items-center text-center">
                    <Link
                        to={SHOP_ROUTE}
                        className="lut-btn-pine inline-flex min-h-12 items-center px-10 py-3 text-base font-semibold shadow-md ring-2 ring-pine-900/15 ring-offset-2 ring-offset-cream-50 transition hover:ring-pine-800/25 touch-manipulation"
                    >
                        Перейти в каталог
                    </Link>
                    <p className="mt-4 max-w-xl text-sm text-bark-600">Недавно добавленные товары</p>
                </div>

                <div className={`mt-8 ${SHOP_ITEM_GRID_CLASS}`}>
                    {recent.length === 0 ? (
                        <p className="text-sm text-bark-500">Не удалось загрузить товары или список пуст.</p>
                    ) : (
                        recent
                            .filter(hasCatalogItemId)
                            .map((item) => <ItemCard key={catalogItemId(item)} item={item}/>)
                    )}
                </div>
            </section>

            <section id="delivery" className="mt-16 scroll-mt-28 border-t border-stone-200/60 pt-12">
                <div className="mx-auto max-w-2xl text-center">
                    <h2 className="text-2xl font-semibold tracking-tight text-bark-900">Доставка</h2>
                    <div className="mt-4 space-y-3 text-sm leading-relaxed text-bark-700">
                        <p>
                            <span className="font-medium text-bark-800">Яндекс.Доставка</span> — заберёте заказ в пункте
                            выдачи или постамате: при оформлении выбираете пункт на карте, стоимость доставки входит в сумму заказа.
                        </p>
                        <p>
                            <span className="font-medium text-bark-800">По договорённости</span> — указываете адрес
                            вручную; способ отправки, срок и стоимость согласуем с вами отдельно (на сайте оплачиваются только
                            товары).
                        </p>
                        <p>
                            Есть вопросы? Пишите нам {' '}
                            <a
                                href={TELEGRAM_URL || 'https://t.me/lutica_candles'}
                                className="font-medium text-pine-800 underline decoration-pine-300 underline-offset-2 hover:text-pine-900"
                                target="_blank"
                                rel="noreferrer"
                            >
                                в Telegram
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
});

export default Landing;
